import {
  Bookmark,
  Bot,
  CheckCircle,
  ExternalLink,
  Heart,
  MessageCircle,
  Monitor,
  Moon,
  MoreHorizontal,
  RefreshCw,
  Send as SendIcon,
  Share2,
  Smartphone,
  Sun,
  Tablet,
  ThumbsUp,
} from "lucide-react";
import { SiGoogle } from "react-icons/si";
import React, { useState } from "react";
import { useActiveContext } from "../../contexts/ActiveContextContext";
import { useTranslation } from "../../contexts/LanguageContext";
import { buildApiUrl } from "../../shared/constants";
import { cn } from "../../utils/cn";
import { getPlatformConfig } from "../../utils/platformIcons";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import Modal from "../ui/Modal";

interface PreviewPostModalProps {
  open: boolean;
  onClose: () => void;
  post: {
    id: number;
    content: string;
    hashtags?: string[];
    call_to_action?: string;
    call_to_action_url?: string;
    image_info?: { filename?: string; public_url?: string; [key: string]: any };
    video_info?: { filename?: string; public_url?: string; [key: string]: any };
    media_gallery?: Array<{
      url?: string;
      public_url?: string;
      type?: "image" | "video";
      [key: string]: any;
    }>;
    platform?: string;
    traffic_source?: "paid" | "organic";
    utm_medium?: string;
    agent_generated?: boolean;
    linkedin_url?: string;
    published_at?: string;
    status?: string;
    failure_reason?: string;
  } | null;
  onApprove?: () => void;
  onEdit?: () => void;
  showActions?: boolean;
}

type DeviceView = "mobile" | "tablet" | "desktop";

const PreviewPostModal: React.FC<PreviewPostModalProps> = ({
  open,
  onClose,
  post,
}) => {
  const { t } = useTranslation();
  const [deviceView, setDeviceView] = useState<DeviceView>("desktop");
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleRetry = async () => {
    if (!post?.id || retrying) return;
    setRetrying(true);
    setRetryResult(null);
    try {
      const token =
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        "";
      const resp = await fetch(buildApiUrl(`/api/posts/${post.id}/republish`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ linkedin_type: "personal" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (
        resp.ok &&
        (data.success || data.results?.some?.((r: any) => r.success))
      ) {
        setRetryResult({
          success: true,
          message: "Publicação realizada com sucesso!",
        });
      } else {
        const errMsg =
          data.error ||
          data.results?.find?.((r: any) => !r.success)?.message ||
          data.message ||
          "Falha ao publicar. Verifique as configurações e tente novamente.";
        setRetryResult({ success: false, message: String(errMsg) });
      }
    } catch (err) {
      setRetryResult({
        success: false,
        message: "Erro de conexão ao tentar publicar.",
      });
    } finally {
      setRetrying(false);
    }
  };

  const nextMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (post?.media_gallery && post.media_gallery.length > 0) {
      setCurrentMediaIndex((prev) => (prev + 1) % post.media_gallery!.length);
    }
  };

  const prevMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (post?.media_gallery && post.media_gallery.length > 0) {
      setCurrentMediaIndex(
        (prev) =>
          (prev - 1 + post.media_gallery!.length) % post.media_gallery!.length,
      );
    }
  };
  const [isDragging, setIsDragging] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [initialScrollTop, setInitialScrollTop] = useState(0);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const progressRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);
  const { activeContext } = useActiveContext();

  if (!post) return null;

  const brandName =
    activeContext.clientCompany?.name ||
    activeContext.organization?.name ||
    t("Sua Marca");
  const brandHandle = brandName.toLowerCase().replace(/\s+/g, ".");

  // Helper to pick the best variation
  const getBestVariationUrl = (platform: string, mediaItem: any) => {
    const isInstagram = platform.toLowerCase().includes("instagram");
    const isFacebook = platform.toLowerCase().includes("facebook");
    const isStories = platform.toLowerCase().includes("stories");

    const variations =
      mediaItem?.image_info?.variations || mediaItem?.variations;
    if (!variations) {
      const url =
        mediaItem?.image_info?.public_url ||
        mediaItem?.public_url ||
        mediaItem?.url;
      if (url) return url;

      const filename = mediaItem?.image_info?.filename || mediaItem?.filename;
      if (filename) return buildApiUrl(`/api/images/${filename}`);
      return null;
    }

    let preferred = "square";
    if (isStories) preferred = "stories";
    else if (isInstagram) preferred = "portrait";
    else if (isFacebook) preferred = "landscape";

    const varData = variations[preferred] || variations["square"];
    if (varData) {
      if (varData.public_url) return varData.public_url;
      if (varData.filename)
        return buildApiUrl(`/api/images/${varData.filename}`);
    }

    return (
      mediaItem?.image_info?.public_url ||
      mediaItem?.public_url ||
      mediaItem?.url
    );
  };
  const brandLogo =
    activeContext.clientCompany?.logo_url ||
    activeContext.organization?.logo_url;
  const brandInitial = brandName.charAt(0).toUpperCase();

  const TRUNCATE_LENGTH = 150;
  const shouldTruncate =
    post.content && post.content.length > TRUNCATE_LENGTH && !isCaptionExpanded;

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  };

  const isPaid = post.traffic_source
    ? post.traffic_source === "paid"
    : post.utm_medium === "cpc" || post.utm_medium === "paid";

  // Posts antigos (bug corrigido no backend) foram salvos com hashtags
  // serializadas como literal de array do Postgres ("{tag1,tag2}") — limpa
  // chaves/# residuais antes de exibir (o "#" já é adicionado no JSX abaixo).
  const cleanHashtag = (tag: string) =>
    tag
      .trim()
      .replace(/^[{]+|[}]+$/g, "")
      .replace(/^#+/, "")
      .trim();

  let hashtagsArray: string[] = [];

  if (Array.isArray(post.hashtags)) {
    hashtagsArray = (post.hashtags as string[])
      .map(cleanHashtag)
      .filter((tag) => tag.length > 0);
  } else if (
    post.hashtags &&
    typeof post.hashtags === "string" &&
    (post.hashtags as string).trim()
  ) {
    hashtagsArray = (post.hashtags as string)
      .split(",")
      .map(cleanHashtag)
      .filter((tag: string) => tag.length > 0);
  }

  const platformLower = post.platform?.toLowerCase() || "";
  const platformConfig = getPlatformConfig(post.platform || "facebook");
  const PlatformIcon = platformConfig.icon;

  const deviceOptions = {
    mobile: {
      wrapper:
        "w-[320px] h-full mx-auto relative transition-all duration-500 ease-in-out",
      body: cn(
        "h-[550px] border-10 border-[#0a0a0c] rounded-[3rem] shadow-2xl overflow-hidden relative transition-all duration-500",
        isDarkMode ? "bg-black" : "bg-white",
      ),
      screen: cn(
        "h-full overflow-y-auto overflow-x-hidden scrollbar-hide py-8 transition-all duration-500",
        isDarkMode ? "bg-[#18191a]" : "bg-[#f8f9fa]",
      ),
      notch:
        "absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#0a0a0c] rounded-b-2xl z-20 flex items-center justify-center gap-1.5",
      homeIndicator: cn(
        "absolute bottom-2 left-1/2 -translate-x-1/2 w-28 h-1.5 rounded-full z-20",
        isDarkMode ? "bg-white/20" : "bg-[#0a0a0c]/10",
      ),
      badge: "hidden",
    },
    tablet: {
      wrapper:
        "w-[450px] h-full mx-auto relative transition-all duration-500 ease-in-out",
      body: cn(
        "h-[560px] border-12 border-[#0a0a0c] rounded-[2.5rem] shadow-2xl overflow-hidden relative transition-all duration-500",
        isDarkMode ? "bg-black" : "bg-white",
      ),
      screen: cn(
        "h-full overflow-y-auto overflow-x-hidden scrollbar-hide py-6 transition-all duration-500",
        isDarkMode ? "bg-[#18191a]" : "bg-[#f8f9fa]",
      ),
      notch: null,
      homeIndicator: cn(
        "absolute bottom-2 left-1/2 -translate-x-1/2 w-20 h-1 rounded-full z-20",
        isDarkMode ? "bg-white/20" : "bg-[#0a0a0c]/10",
      ),
      badge: "hidden",
    },
    desktop: {
      wrapper:
        "w-full max-w-[850px] mx-auto transition-all duration-500 ease-in-out",
      body: cn(
        "border rounded-xl shadow-xl overflow-hidden transition-all duration-500",
        isDarkMode ? "bg-black border-gray-800" : "bg-white border-gray-200",
      ),
      screen: "max-h-[520px] overflow-y-auto scrollbar-hide",
      notch: null,
      homeIndicator: null,
      badge: "flex",
    },
  };

  const isFailed = post.status === "failed";

  // Drag-to-Scroll Logic Optimized
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    isDraggingRef.current = true;
    setIsDragging(true); // Still using state for cursor visual feedback
    setStartY(e.pageY - scrollRef.current.offsetTop);
    setInitialScrollTop(scrollRef.current.scrollTop);
  };

  const handleMouseMove = (e: React.MouseEvent | MouseEvent) => {
    if (!isDraggingRef.current || !scrollRef.current) return;

    // Use MouseEvent type check for global listeners if needed
    const pageY = (e as React.MouseEvent).pageY || (e as MouseEvent).pageY;

    const y = pageY - scrollRef.current.offsetTop;
    const walk = (y - startY) * 1.5;
    scrollRef.current.scrollTop = initialScrollTop - walk;
    // updateProgress happens in onScroll event
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  const updateScrollProgress = () => {
    if (!scrollRef.current || !progressRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    // Direct DOM manipulation to avoid state re-renders on each pixel
    progressRef.current.style.width = `${Math.min(100, Math.max(0, progress))}%`;
  };

  const renderMedia = (platform?: string) => {
    const isInstagram = platform?.toLowerCase().includes("instagram");
    const hasGallery = post.media_gallery && post.media_gallery.length > 0;

    if (hasGallery) {
      const currentItem = post.media_gallery![currentMediaIndex];
      const isVideo = currentItem.type === "video";

      return (
        <div
          className={cn(
            "relative bg-black transition-all group/preview-media w-full",
            isInstagram ? "aspect-square" : "aspect-video",
          )}
        >
          {isVideo ? (
            <video
              src={currentItem.public_url || currentItem.url}
              controls
              className="w-full h-full object-contain"
              preload="metadata"
              crossOrigin="anonymous"
              key={`preview-video-${currentMediaIndex}`}
            />
          ) : (
            <img
              src={getBestVariationUrl(platform || "facebook", currentItem)}
              alt={t("Mídia {index}", { index: currentMediaIndex + 1 })}
              className="w-full h-full object-contain animate-fade-in"
              draggable={false}
            />
          )}

          {/* Navegação do Carrossel de Preview */}
          {post.media_gallery!.length > 1 && (
            <>
              {/* Indicador Numérico */}
              <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/50 backdrop-blur-md text-white text-[10px] font-bold z-10 border border-white/10">
                {currentMediaIndex + 1} / {post.media_gallery!.length}
              </div>

              {/* Botões de Navegação */}
              <button
                onClick={prevMedia}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-xs opacity-0 group-hover/preview-media:opacity-100 transition-all z-20"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M15 19l-7-7 7-7"
                  ></path>
                </svg>
              </button>

              <button
                onClick={nextMedia}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-xs opacity-0 group-hover/preview-media:opacity-100 transition-all z-20"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </button>

              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {post.media_gallery!.map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "w-1.5 h-1.5 rounded-full transition-all duration-300",
                      i === currentMediaIndex ? "bg-white w-3" : "bg-white/40",
                    )}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      );
    }

    // Legado: Vídeo Único
    if (
      post.video_info &&
      (post.video_info.filename || post.video_info.public_url)
    ) {
      return (
        <div
          className={cn(
            "relative bg-black transition-all group w-full",
            isInstagram ? "aspect-square" : "aspect-video",
          )}
        >
          <video
            src={
              post.video_info.public_url ||
              buildApiUrl(`/api/videos/${post.video_info.filename}`)
            }
            controls
            className="w-full h-full object-contain"
            preload="metadata"
            crossOrigin="anonymous"
          >
            {t("Seu navegador não suporta a tag de vídeo.")}
          </video>
        </div>
      );
    }

    // Legado: Imagem Única
    if (
      post.image_info &&
      (post.image_info.filename || post.image_info.public_url)
    ) {
      return (
        <div
          className={cn(
            "relative w-full bg-gray-50 overflow-hidden group flex items-center justify-center transition-all",
            isInstagram ? "aspect-square" : "h-auto max-h-[450px]",
          )}
        >
          <img
            src={getBestVariationUrl(platform || "facebook", post)}
            alt={t("Imagem do post")}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>
      );
    }
    return null;
  };

  const renderPlatformSimulation = () => {
    // Google Ads Simulation
    if (platformLower.includes("google_ads")) {
      return (
        <div
          className={cn(
            "w-full font-sans transition-all duration-500 flex flex-col gap-6",
            isDarkMode
              ? "bg-[#171717] text-white"
              : "bg-[#f8f9fa] text-[#202124]",
          )}
        >
          {/* Versão 1: Search Result Preview */}
          <div
            className={cn(
              "p-5 rounded-2xl border shadow-xs",
              isDarkMode
                ? "bg-[#202124] border-white/5"
                : "bg-white border-gray-200",
            )}
          >
            <div className="flex items-center justify-between mb-3 text-[11px] font-medium text-gray-500 tracking-tight uppercase">
              <div className="flex items-center gap-1.5">
                <div className="bg-[#fbbc04] text-white px-1.5 py-0.5 rounded-sm text-[9px] font-bold">
                  {t("Anúncio")}
                </div>
                <span>{t("Google Search")}</span>
              </div>
              <span>{post.call_to_action_url || "google.com.br"}</span>
            </div>
            <h3 className="text-[#1a0dab] dark:text-[#8ab4f8] text-[20px] leading-tight hover:underline cursor-pointer mb-1 font-medium">
              {brandName}:{" "}
              {post.call_to_action || t("Solução Completa para Negócios")}
            </h3>
            <p
              className={cn(
                "text-[14px] leading-relaxed",
                isDarkMode ? "text-[#bdc1c6]" : "text-[#4d5156]",
              )}
            >
              {post.content}
            </p>
          </div>

          {/* Versão 2: Display / PMax / Discovery Style */}
          <div
            className={cn(
              "rounded-2xl border shadow-lg overflow-hidden",
              isDarkMode
                ? "bg-[#202124] border-white/5"
                : "bg-white border-gray-100",
            )}
          >
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SiGoogle className="text-amber-500 h-4 w-4" />
                <span className="text-[12px] font-extrabold uppercase tracking-widest opacity-60">
                  {t("Google Ads Performance")}
                </span>
              </div>
              <Badge variant="info" size="sm">
                {t("PMax / Discovery")}
              </Badge>
            </div>

            {renderMedia("google_ads")}

            <div className="p-4 bg-linear-to-b from-transparent to-black/5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center p-1 shadow-xs overflow-hidden">
                  {brandLogo ? (
                    <img
                      src={brandLogo}
                      alt=""
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-[8px] font-black text-amber-600">
                      {brandInitial}
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-tighter">
                  {brandName}
                </span>
              </div>

              <h4 className="text-[16px] font-bold mb-1.5 leading-tight">
                {post.call_to_action || t("Título do Anúncio")}
              </h4>
              <p
                className={cn(
                  "text-[13px] leading-relaxed line-clamp-2",
                  isDarkMode ? "text-gray-400" : "text-gray-600",
                )}
              >
                {post.content}
              </p>

              {post.call_to_action && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-[10px] text-gray-400 font-medium">
                    {t("Anúncio")} • google.com
                  </div>
                  <button className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-bold rounded-full transition-all shadow-md active:scale-95 uppercase tracking-wide">
                    {post.call_to_action}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Facebook Simulation
    if (platformLower.includes("facebook") || platformLower.includes("fb")) {
      return (
        <div
          className={cn(
            "w-full font-sans transition-all duration-500",
            isDarkMode
              ? "bg-[#242526] text-[#e4e6eb]"
              : "bg-white text-[#050505]",
          )}
        >
          {/* Header Browser (Desktop only) */}
          {deviceView === "desktop" && (
            <div
              className={cn(
                "h-4 border-b flex items-center px-3 gap-1",
                isDarkMode
                  ? "bg-[#18191a] border-white/10"
                  : "bg-[#f0f2f5] border-gray-200",
              )}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
            </div>
          )}

          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-white ring-1 overflow-hidden",
                  isDarkMode
                    ? "bg-gray-800 ring-white/10"
                    : "bg-blue-600 ring-black/5",
                )}
              >
                {brandLogo ? (
                  <img
                    src={brandLogo}
                    alt=""
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <span className="font-bold text-lg">{brandInitial}</span>
                )}
              </div>
              <div>
                <div
                  className={cn(
                    "font-bold text-[15px] flex items-center gap-1 leading-tight",
                    isDarkMode ? "text-white" : "text-[#050505]",
                  )}
                >
                  {brandName}
                </div>
                <div
                  className={cn(
                    "text-[13px] flex items-center gap-1 leading-tight",
                    isDarkMode ? "text-[#b0b3b8]" : "text-[#65676b]",
                  )}
                >
                  {post.published_at
                    ? formatDate(post.published_at)
                    : t("Agora mesmo")}{" "}
                  • 🌎
                  {isPaid && (
                    <span
                      className={cn(
                        "font-normal border-l pl-1 ml-0.5",
                        isDarkMode ? "border-gray-700" : "border-gray-300",
                      )}
                    >
                      {t("Patrocinado")}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <MoreHorizontal
              className={cn(
                "h-5 w-5 cursor-pointer rounded-full",
                isDarkMode
                  ? "text-[#b0b3b8] hover:bg-white/10"
                  : "text-[#65676b] hover:bg-gray-100",
              )}
            />
          </div>

          <div className="px-4 pb-3 text-[15px] leading-normal whitespace-pre-wrap wrap-break-word">
            {post.content}
            {hashtagsArray.length > 0 && (
              <div
                className={cn(
                  "mt-2 font-medium flex flex-wrap gap-x-1.5 gap-y-0.5",
                  isDarkMode ? "text-[#4591ff]" : "text-[#216fdb]",
                )}
              >
                {hashtagsArray.slice(0, 15).map((tag, i) => (
                  <span key={i} className="hover:underline cursor-pointer">
                    {tag.startsWith("#") ? tag : `#${tag}`}
                  </span>
                ))}
                {hashtagsArray.length > 15 && (
                  <span className="text-gray-400">...</span>
                )}
              </div>
            )}
          </div>

          {renderMedia("facebook")}

          {post.call_to_action && (
            <div
              className={cn(
                "px-4 py-3 flex justify-between items-center border-t cursor-pointer group",
                isDarkMode
                  ? "bg-[#2f3031] border-white/5"
                  : "bg-[#f0f2f5] border-gray-200",
              )}
            >
              <div className="flex-1 min-w-0 pr-4">
                <div
                  className={cn(
                    "text-[13px] uppercase tracking-wide truncate",
                    isDarkMode ? "text-[#b0b3b8]" : "text-[#65676b]",
                  )}
                >
                  {post.call_to_action_url || t("SITE DA MARCA")}
                </div>
                <div
                  className={cn(
                    "font-bold text-base truncate",
                    isDarkMode ? "text-white" : "text-[#050505]",
                  )}
                >
                  {post.call_to_action}
                </div>
              </div>
              <Button
                size="sm"
                className={cn(
                  "font-bold border-none h-9 px-4 rounded-md shrink-0",
                  isDarkMode
                    ? "bg-white/10 text-white hover:bg-white/20"
                    : "bg-[#e4e6eb] text-[#050505] hover:bg-[#d8dadf]",
                )}
              >
                {post.call_to_action}
              </Button>
            </div>
          )}

          <div className="mx-4 py-3 flex items-center justify-between border-b border-[#dadde1] gap-2">
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="flex -space-x-1">
                <div className="bg-[#1877f2] rounded-full p-1 ring-1 ring-white z-10">
                  <ThumbsUp className="h-2.5 w-2.5 text-white fill-white" />
                </div>
                <div className="bg-[#f33e58] rounded-full p-1 ring-1 ring-white">
                  <Heart className="h-2.5 w-2.5 text-white fill-white" />
                </div>
              </div>
              <span className="text-[13px] text-[#65676b]">
                {isLiked ? "125" : "124"}
              </span>
            </div>
            <div className="text-[13px] text-[#65676b] hover:underline cursor-pointer truncate text-right">
              {t("12 coment. • 8 partilh.")}
            </div>
          </div>

          <div className="px-1 py-1 flex items-center justify-around text-[#65676b] font-semibold">
            <button
              onClick={() => setIsLiked(!isLiked)}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-1.5 hover:bg-[#f2f2f2] rounded-md transition-all text-[14px] active:scale-90",
                isLiked ? "text-[#1877f2]" : "",
              )}
            >
              <ThumbsUp
                className={cn(
                  "h-[18px] w-[18px]",
                  isLiked ? "fill-[#1877f2]" : "",
                )}
              />{" "}
              {t("Curtir")}
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-1.5 hover:bg-[#f2f2f2] rounded-md transition-colors text-[14px]">
              <MessageCircle className="h-[18px] w-[18px]" /> {t("Comentar")}
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-1.5 hover:bg-[#f2f2f2] rounded-md transition-colors text-[14px]">
              <Share2 className="h-[18px] w-[18px]" /> {t("Enviar")}
            </button>
          </div>
        </div>
      );
    }

    // Instagram Simulation
    if (platformLower.includes("instagram") || platformLower.includes("ig")) {
      return (
        <div
          className={cn(
            "w-full font-sans transition-all duration-500",
            isDarkMode ? "bg-black text-white" : "bg-white text-black",
          )}
        >
          {/* Header Browser (Desktop only) */}
          {deviceView === "desktop" && (
            <div
              className={cn(
                "h-4 border-b flex items-center px-3 gap-1",
                isDarkMode
                  ? "bg-black border-white/10"
                  : "bg-gray-50 border-gray-100",
              )}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
            </div>
          )}
          <div className="flex items-center justify-between px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-[34px] h-[34px] rounded-full p-[1.5px] bg-linear-to-tr from-[#FFD600] via-[#FF7A00] via-[#FF0069] via-[#D300C5] to-[#7638FA]">
                <div className="w-full h-full bg-white rounded-full p-[1.5px]">
                  <div className="w-full h-full bg-gray-100 rounded-full border border-black/5 overflow-hidden flex items-center justify-center">
                    {brandLogo ? (
                      <img
                        src={brandLogo}
                        alt=""
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <span className="text-[10px] font-black uppercase text-gray-500">
                        {brandInitial}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <div className="font-bold text-[13px] tracking-tight leading-tight flex items-center gap-1">
                  {brandHandle}
                  {isPaid && (
                    <span className="text-[11px] font-normal text-gray-400">
                      • {t("Patrocinado")}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-900 leading-tight">
                  {t("Áudio original")}
                </div>
              </div>
            </div>
            <MoreHorizontal
              className={cn(
                "h-5 w-5 cursor-pointer",
                isDarkMode ? "text-white" : "text-black",
              )}
            />
          </div>

          {renderMedia("instagram")}

          <div
            className={cn("px-3 pt-3", isDarkMode ? "bg-black" : "bg-white")}
          >
            <div
              className={cn(
                "flex items-center justify-between mb-3",
                isDarkMode ? "text-white" : "text-black",
              )}
            >
              <div className="flex gap-4">
                <Heart
                  onClick={() => setIsLiked(!isLiked)}
                  className={cn(
                    "h-[24px] w-[24px] cursor-pointer transition-all active:scale-75",
                    isLiked
                      ? "text-[#ed4956] fill-[#ed4956]"
                      : isDarkMode
                        ? "text-white/70 hover:text-white"
                        : "hover:text-black/50",
                  )}
                />
                <MessageCircle
                  className={cn(
                    "h-[24px] w-[24px] transition-colors",
                    isDarkMode
                      ? "text-white/70 hover:text-white"
                      : "hover:text-black/50",
                  )}
                />
                <SendIcon
                  className={cn(
                    "rotate-15 h-[22px] w-[22px] transition-colors",
                    isDarkMode
                      ? "text-white/70 hover:text-white"
                      : "hover:text-black/50",
                  )}
                />
              </div>
              <Bookmark
                className={cn(
                  "h-[24px] w-[24px] transition-colors",
                  isDarkMode
                    ? "text-white/70 hover:text-white"
                    : "hover:text-black/50",
                )}
              />
            </div>

            <div
              className={cn(
                "font-bold text-[14px] mb-2 leading-none",
                isDarkMode ? "text-white" : "text-black",
              )}
            >
              {t("{count} curtidas", { count: isLiked ? "1.438" : "1.437" })}
            </div>

            <div
              className={cn(
                "text-[14px] leading-relaxed mb-2",
                isDarkMode ? "text-white" : "text-black",
              )}
            >
              <span className="font-extrabold mr-1.5">{brandHandle}</span>
              {shouldTruncate ? (
                <>
                  {post.content.substring(0, TRUNCATE_LENGTH)}
                  <button
                    onClick={() => setIsCaptionExpanded(true)}
                    className={cn(
                      "ml-1 font-medium bg-transparent border-none p-0",
                      isDarkMode ? "text-gray-400" : "text-gray-500",
                    )}
                  >
                    ... {t("mais")}
                  </button>
                </>
              ) : (
                post.content
              )}

              {hashtagsArray.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-x-1.5 gap-y-0.5">
                  {hashtagsArray
                    .slice(0, isCaptionExpanded ? undefined : 8)
                    .map((tag, i) => (
                      <span
                        key={i}
                        className={cn(
                          "cursor-pointer hover:opacity-70",
                          isDarkMode ? "text-blue-400" : "text-[#00376b]",
                        )}
                      >
                        {tag.startsWith("#") ? tag : `#${tag}`}
                      </span>
                    ))}
                  {!isCaptionExpanded && hashtagsArray.length > 8 && (
                    <button
                      onClick={() => setIsCaptionExpanded(true)}
                      className={cn(
                        "font-medium",
                        isDarkMode ? "text-gray-400" : "text-gray-500",
                      )}
                    >
                      ... {t("mais")}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div
              className={cn(
                "text-[10px] font-medium uppercase tracking-wide mt-1",
                isDarkMode ? "text-gray-400" : "text-gray-500",
              )}
            >
              {t("HÁ 2 HORAS")}
            </div>
          </div>

          {post.call_to_action && (
            <div className="bg-[#0095f6] active:bg-[#0081d6] text-white h-[44px] flex items-center justify-between px-4 mt-3 cursor-pointer transition-colors select-none">
              <span className="font-bold text-[14px]">
                {post.call_to_action}
              </span>
              <ExternalLink className="h-4 w-4" />
            </div>
          )}
        </div>
      );
    }

    // Google My Business Simulation
    if (
      platformLower === "google_my_business" ||
      platformLower.includes("gmb")
    ) {
      return (
        <div
          className={cn(
            "w-full font-sans transition-all duration-500",
            isDarkMode
              ? "bg-[#202124] text-[#e8eaed]"
              : "bg-white text-[#202124]",
          )}
        >
          {/* Header Browser (Desktop only) */}
          {deviceView === "desktop" && (
            <div
              className={cn(
                "h-4 border-b flex items-center px-3 gap-1",
                isDarkMode
                  ? "bg-[#202124] border-white/5"
                  : "bg-gray-50 border-gray-100",
              )}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
            </div>
          )}
          <div className="p-4 flex items-center gap-3">
            <div
              className={cn(
                "w-10 h-10 rounded-full border flex items-center justify-center overflow-hidden shadow-xs shrink-0",
                isDarkMode
                  ? "bg-[#3c4043] border-white/10"
                  : "bg-white border-gray-100",
              )}
            >
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <span
                  className={cn(
                    "font-bold text-lg",
                    isDarkMode ? "text-[#8ab4f8]" : "text-[#1a73e8]",
                  )}
                >
                  {brandInitial}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={cn(
                  "font-bold text-[15px] flex items-center gap-1.5 leading-tight",
                  isDarkMode ? "text-white" : "text-gray-900",
                )}
              >
                <span className="truncate">{brandName}</span>
                <div className="bg-[#1a73e8] rounded-full p-0.5 shrink-0">
                  <ThumbsUp className="h-2 w-2 text-white fill-white" />
                </div>
              </div>
              <div
                className={cn(
                  "text-[12px] mt-0.5",
                  isDarkMode ? "text-gray-400" : "text-gray-500",
                )}
              >
                {t("Postagem do Google")} •{" "}
                {post.published_at
                  ? formatDate(post.published_at)
                  : t("Agora mesmo")}
              </div>
            </div>
            <MoreHorizontal
              className={cn(
                "h-5 w-5 cursor-pointer rounded-full p-0.5",
                isDarkMode
                  ? "text-gray-400 hover:bg-white/10"
                  : "text-gray-400 hover:bg-gray-100",
              )}
            />
          </div>

          <div className="px-5 pb-3 pt-1 text-[15px] leading-relaxed whitespace-pre-wrap wrap-break-word">
            <span className={isDarkMode ? "text-[#e8eaed]" : "text-[#3c4043]"}>
              {post.content}
            </span>
            {hashtagsArray.length > 0 && (
              <div
                className={cn(
                  "mt-3 font-medium flex flex-wrap gap-x-2 gap-y-1",
                  isDarkMode ? "text-[#8ab4f8]" : "text-[#1a73e8]",
                )}
              >
                {hashtagsArray.slice(0, 12).map((tag, i) => (
                  <span key={i} className="hover:underline cursor-pointer">
                    {tag.startsWith("#") ? tag : `#${tag}`}
                  </span>
                ))}
                {hashtagsArray.length > 12 && (
                  <span className="text-gray-400">...</span>
                )}
              </div>
            )}
          </div>

          {renderMedia("google_my_business")}

          {post.call_to_action && (
            <div className="p-5 pt-3 border-t border-gray-50 flex justify-center w-full">
              <div className="w-full h-10 px-6 bg-[#1a73e8] hover:bg-[#1765cc] active:bg-[#1557b0] text-white font-bold rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer select-none overflow-hidden text-center">
                <span className="text-[13px] leading-none truncate block min-w-0">
                  {post.call_to_action}
                </span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-90" />
              </div>
            </div>
          )}
        </div>
      );
    }

    // LinkedIn & Fallback Simulation (Modern Style)
    return (
      <div
        className={cn(
          "w-full font-sans shadow-xs transition-all duration-500",
          isDarkMode ? "bg-[#1d2226] text-white" : "bg-white text-gray-900",
        )}
      >
        {/* Header Browser (Desktop only) */}
        {deviceView === "desktop" && (
          <div
            className={cn(
              "h-4 border-b flex items-center px-3 gap-1",
              isDarkMode
                ? "bg-[#18191a] border-white/10"
                : "bg-[#f3f6f8] border-gray-200",
            )}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
          </div>
        )}
        <div className="flex items-start justify-between p-4 pb-2">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div
              className={cn(
                "w-12 h-12 rounded-xs border flex items-center justify-center overflow-hidden shrink-0 shadow-xs",
                isDarkMode
                  ? "bg-[#283038] border-white/10"
                  : "bg-gray-50 border-gray-100",
              )}
            >
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <span
                  className={cn(
                    "font-bold text-xl",
                    isDarkMode ? "text-gray-400" : "text-gray-400",
                  )}
                >
                  {brandInitial}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div
                className={cn(
                  "font-bold text-[14px] hover:underline cursor-pointer flex items-center gap-1",
                  isDarkMode
                    ? "text-white hover:text-[#70b1ff]"
                    : "text-gray-900 hover:text-[#0a66c2]",
                )}
              >
                <span className="truncate">{brandName}</span>
                <span
                  className={cn(
                    "text-[12px] font-normal shrink-0",
                    isDarkMode ? "text-gray-400" : "text-gray-500",
                  )}
                >
                  • {t("2º")}
                </span>
              </div>
              <div
                className={cn(
                  "text-[12px] truncate mt-0.5",
                  isDarkMode ? "text-gray-400" : "text-gray-500",
                )}
              >
                {t("Simulação de feed profissional")}
              </div>
              <div
                className={cn(
                  "text-[12px] flex items-center gap-1 mt-0.5",
                  isDarkMode ? "text-gray-400" : "text-gray-500",
                )}
              >
                {post.published_at
                  ? formatDate(post.published_at)
                  : t("Agora mesmo")}{" "}
                • 🌎
                {isPaid && (
                  <span
                    className={cn(
                      "font-bold",
                      isDarkMode ? "text-[#70b1ff]" : "text-[#0a66c2]",
                    )}
                  >
                    {" "}
                    • {t("Promovido")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsFollowing(!isFollowing)}
            className={cn(
              "font-bold text-[14px] px-3 py-1.5 rounded-full flex items-center transition-all shrink-0 ml-2 active:scale-95",
              isFollowing
                ? isDarkMode
                  ? "bg-white/5 text-gray-400 border border-white/10"
                  : "bg-white text-gray-400 border border-gray-200"
                : isDarkMode
                  ? "text-[#70b1ff] hover:bg-white/5"
                  : "text-[#0a66c2] hover:bg-blue-50",
            )}
          >
            {isFollowing ? `✓ ${t("Seguindo")}` : `+ ${t("Seguir")}`}
          </button>
        </div>

        <div
          className={cn(
            "px-4 pb-3 pt-1 text-[14px] leading-relaxed whitespace-pre-wrap wrap-break-word",
            isDarkMode ? "text-white/90" : "text-gray-800",
          )}
        >
          {post.content}
          {hashtagsArray.length > 0 && (
            <div className="mt-2 text-[#0a66c2] font-semibold flex flex-wrap gap-x-1.5 gap-y-0.5">
              {hashtagsArray.slice(0, 15).map((tag, i) => (
                <span key={i} className="hover:underline cursor-pointer">
                  {tag.startsWith("#") ? tag : `#${tag}`}
                </span>
              ))}
              {hashtagsArray.length > 15 && (
                <span className="text-gray-400">...</span>
              )}
            </div>
          )}
        </div>

        {renderMedia("linkedin")}

        {post.call_to_action && (
          <div
            className={cn(
              "p-3 flex justify-between items-center border-t border-b gap-3",
              isDarkMode
                ? "bg-[#1d2226] border-white/5"
                : "bg-gray-50 border-gray-100",
            )}
          >
            <div
              className={cn(
                "text-[14px] font-bold pr-2 truncate min-w-0 flex-1",
                isDarkMode ? "text-white/80" : "text-gray-900",
              )}
            >
              {post.call_to_action_url || t("virtusautomation.com.br")}
            </div>
            <div
              className={cn(
                "rounded-full border border-[#0a66c2] text-[#0a66c2] font-bold text-[13px] px-4 py-1 shrink-0 transition-all cursor-pointer whitespace-nowrap min-w-[90px] text-center",
                isDarkMode ? "hover:bg-[#0a66c2]/10" : "hover:bg-blue-50",
              )}
            >
              {post.call_to_action}
            </div>
          </div>
        )}

        <div className="mx-4 py-2 flex items-center justify-between border-b border-gray-100 text-[12px] text-gray-500 font-medium gap-2">
          <div className="flex items-center gap-1 shrink-0">
            <div className="flex -space-x-1">
              <div className="bg-[#0a66c2] rounded-full p-[3px] border border-white z-10">
                <ThumbsUp className="h-2.5 w-2.5 text-white fill-white" />
              </div>
              <div className="bg-[#5ea933] rounded-full p-[3px] border border-white">
                <SendIcon className="h-2.5 w-2.5 text-white fill-white" />
              </div>
            </div>
            <span>{isLiked ? "1.103" : "1.102"}</span>
          </div>
          <div className="hover:text-[#0a66c2] hover:underline cursor-pointer truncate text-right">
            {t("45 coment. • 12 partilh.")}
          </div>
        </div>

        <div
          className={cn(
            "px-1 py-1 flex items-center justify-between font-bold text-[12px]",
            isDarkMode ? "text-white/70" : "text-gray-600",
          )}
        >
          <button
            onClick={() => setIsLiked(!isLiked)}
            className={cn(
              "flex-1 flex items-center justify-center gap-1 py-3 hover:bg-black/5 rounded-md transition-all active:scale-90 whitespace-nowrap",
              isLiked
                ? "text-[#70b1ff]"
                : isDarkMode
                  ? "hover:bg-white/5"
                  : "hover:bg-black/5",
            )}
          >
            <ThumbsUp
              className={cn(
                "h-4 w-4 shrink-0",
                isLiked ? "fill-[#70b1ff]" : "",
              )}
            />{" "}
            {t("Curtir")}
          </button>
          <button
            className={cn(
              "flex-1 flex items-center justify-center gap-1 py-3 rounded-md transition-colors whitespace-nowrap",
              isDarkMode ? "hover:bg-white/5" : "hover:bg-black/5",
            )}
          >
            <MessageCircle className="h-4 w-4 shrink-0" /> {t("Comentar")}
          </button>
          <button
            className={cn(
              "flex-1 flex items-center justify-center gap-1 py-3 rounded-md transition-colors whitespace-nowrap",
              isDarkMode ? "hover:bg-white/5" : "hover:bg-black/5",
            )}
          >
            <Share2 className="h-4 w-4 shrink-0 rotate-12" />{" "}
            {t("Compartilhar")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnOverlayClick
      title={
        <div className="flex items-center gap-3">
          <PlatformIcon className={cn("h-6 w-6", platformConfig.color)} />
          <div>
            <span className="font-bold text-white-900 leading-none">
              {t("Preview da Publicação")}
            </span>
            {isFailed && (
              <div className="text-[10px] font-bold text-red-600 uppercase tracking-wider mt-0.5">
                {t("Falha na Publicação")}
              </div>
            )}
          </div>
        </div>
      }
      subtitle={t("Status atual: {status}", {
        status: post?.status || t("Pendente"),
      })}
      headerVariant="gradient"
      maxWidth="max-w-6xl"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button
            type="button"
            onClick={onClose}
            variant="secondary"
            size="sm"
            className="rounded-xl px-6"
          >
            {t("Fechar")}
          </Button>
        </div>
      }
    >
      <div className="p-0 space-y-3">
        {isFailed && post.failure_reason && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-4 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="p-3 bg-red-100 rounded-xl text-red-600">
              <Bot className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-red-900 uppercase tracking-tight mb-1">
                {t("Ops! Ocorreu um erro na publicação")}
              </h4>
              <p className="text-sm text-red-700 font-medium leading-relaxed">
                {retryResult ? retryResult.message : post.failure_reason}
              </p>
              {retryResult?.success && (
                <p className="text-xs text-green-700 font-semibold mt-1 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Publicado com sucesso!
                </p>
              )}
              <button
                onClick={handleRetry}
                disabled={retrying || retryResult?.success === true}
                className="mt-3 flex items-center gap-2 text-xs font-bold text-red-700 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <RefreshCw
                  className={`h-3 w-3 ${retrying ? "animate-spin" : ""}`}
                />
                {retrying ? "Publicando..." : "Tentar publicar novamente"}
              </button>
            </div>
          </div>
        )}
        {/* Integrated Toolbar - Merged with info to save vertical space */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-purple-100 bg-white/80 backdrop-blur-md sticky top-0 z-30">
          {/* Left: Platform & Secondary Info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className={cn(
                "p-1.5 rounded-lg border shadow-xs shrink-0 transition-all duration-300",
                platformConfig.bgColor,
                platformConfig.borderColor,
              )}
            >
              <PlatformIcon className={cn("h-4 w-4", platformConfig.color)} />
            </div>
            <div className="min-w-0">
              <span className="font-black text-gray-800 text-[13px] tracking-tight uppercase truncate block">
                {platformConfig.name}
              </span>
              <div className="flex items-center gap-2 text-[9px] text-gray-400 font-bold tracking-widest leading-none mt-0.5 whitespace-nowrap">
                {post.published_at && (
                  <span className="flex items-center gap-1">
                    📅 {formatDate(post.published_at)}
                  </span>
                )}
                {(post.image_info || post.video_info) && (
                  <span className="hidden sm:inline-flex items-center gap-1">
                    • ✅ {t("MÍDIA")}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Center: Device Toggles (More Compact) */}
          <div className="flex items-center bg-gray-100/80 p-1 rounded-xl border border-gray-200/50 shadow-inner mx-2">
            <button
              onClick={() => setDeviceView("mobile")}
              className={cn(
                "p-1.5 rounded-lg transition-all duration-300",
                deviceView === "mobile"
                  ? "bg-white shadow-sm text-purple-600"
                  : "text-gray-400 hover:text-gray-600",
              )}
              title={t("Mobile View")}
            >
              <Smartphone className="h-4 w-4" />
            </button>
            <button
              onClick={() => setDeviceView("tablet")}
              className={cn(
                "p-1.5 rounded-lg transition-all duration-300",
                deviceView === "tablet"
                  ? "bg-white shadow-sm text-purple-600"
                  : "text-gray-400 hover:text-gray-600",
              )}
              title={t("Tablet View")}
            >
              <Tablet className="h-4 w-4" />
            </button>
            <button
              onClick={() => setDeviceView("desktop")}
              className={cn(
                "p-1.5 rounded-lg transition-all duration-300",
                deviceView === "desktop"
                  ? "bg-white shadow-sm text-purple-600"
                  : "text-gray-400 hover:text-gray-600",
              )}
              title={t("Desktop View")}
            >
              <Monitor className="h-4 w-4" />
            </button>
            <div className="w-px h-4 bg-gray-300 mx-1" />
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={cn(
                "p-1.5 rounded-lg transition-all duration-300",
                isDarkMode
                  ? "bg-purple-600 text-white shadow-lg"
                  : "text-gray-400 hover:text-gray-600",
              )}
              title={isDarkMode ? t("Light Mode") : t("Dark Mode")}
            >
              {isDarkMode ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Right: Badges */}
          <div className="flex items-center gap-2 justify-end flex-1">
            <Badge
              variant={isPaid ? "info" : "success"}
              size="sm"
              className="font-extrabold uppercase text-[9px] tracking-widest px-2 py-1 rounded-lg"
            >
              {isPaid ? t("PAGO") : t("ORG")}
            </Badge>
            {post.agent_generated && (
              <Badge
                variant="purple"
                size="sm"
                className="hidden sm:flex font-extrabold uppercase text-[9px] tracking-widest px-2 py-1 rounded-lg border border-purple-200"
              >
                <Bot className="h-3 w-3 mr-1" />
                <span>{t("IA")}</span>
              </Badge>
            )}
          </div>
        </div>

        {/* Simulation Area - Constant Height to avoid modal jumps */}
        <div
          className="relative min-h-[600px] flex items-center justify-center p-2 md:p-4 overflow-hidden rounded-xl border border-purple-100/30 bg-[#f8f9fc] bg-[radial-gradient(#e5e7eb_.5px,transparent_.5px)] bg-size-[24px_24px] transition-all duration-500"
          onMouseLeave={stopDragging}
        >
          {/* Progress Bar (Subtle) - Optimized via Ref */}
          <div className="absolute top-0 left-0 w-full h-[3px] bg-gray-100 z-40 overflow-hidden">
            <div
              ref={progressRef}
              className="h-full bg-linear-to-r from-purple-500 to-blue-500 transition-all duration-300 ease-out w-0"
            />
          </div>

          <div
            key={deviceView}
            className={cn(
              deviceOptions[deviceView].wrapper,
              "flex items-center self-center animate-in fade-in slide-in-from-bottom-4 duration-500",
            )}
          >
            <div
              className={cn(
                deviceOptions[deviceView].body,
                "w-full transition-all duration-500",
              )}
            >
              {deviceOptions[deviceView].notch && (
                <div className={deviceOptions[deviceView].notch}>
                  <div className="w-1 h-1 rounded-full bg-slate-800" />
                  <div className="w-6 h-0.5 rounded-full bg-slate-800" />
                </div>
              )}
              <div
                ref={scrollRef}
                className={cn(
                  deviceOptions[deviceView].screen,
                  isDragging ? "cursor-grabbing select-none" : "cursor-grab",
                )}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={stopDragging}
                onScroll={updateScrollProgress}
              >
                {renderPlatformSimulation()}
              </div>
              {deviceOptions[deviceView].homeIndicator && (
                <div className={deviceOptions[deviceView].homeIndicator} />
              )}
            </div>
          </div>

          {/* Highly Subtle Decor */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-purple-200/5 rounded-full blur-3xl -z-10 pointer-events-none" />
        </div>
      </div>
    </Modal>
  );
};

export default PreviewPostModal;
